import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOfficeReady } from "./hooks/useOfficeReady";
import { useAuthManager, AUTH_STATUS } from "./hooks/useAuthManager";
import { useProjectData } from "./hooks/useProjectData";
import { useHashRouter, ROUTES } from "./navigation/useHashRouter";
import { openSetupDialog, setupHash } from "./structure/dialogBridge";
import StructureTab from "./components/projectSetup/StructureTab";
import BasesManager from "./components/BasesManager";
import BOQManager from "./components/BOQManager";
import ProfilePage from "./components/ProfilePage";
import SettingsPage from "./components/SettingsPage";
import { useProductTables } from "./hooks/useProductTables";
import SheetLookupStandalone from "./SheetLookupStandalone";

import LoginScreen from "./components/LoginScreen";
import AddinUpdatePage from "./components/AddinUpdatePage";
import ProjectSelection from "./components/ProjectSelection";
import TabHeader from "./components/TabHeader";
import LogPage from "./components/LogPage";
import PdfExportLauncher from "./components/pdfExport/PdfExportLauncher";
import OpenSheetsPage from "./components/OpenSheetsPage";
import DeleteProjectPage from "./components/DeleteProjectPage";
import AppErrorLogPage from "./components/AppErrorLogPage";
import AdminPanelPage from "./components/AdminPanelPage";
import ImportSheetPage from "./components/ImportSheetPage";
import ExportVersionPage from "./components/ExportVersionPage";
import AutoEvaluatePage from "./components/AutoEvaluatePage";
import IndexationPanel from "./components/IndexationPanel";
import AnalyticsPage from "./components/AnalyticsPage";
import ProjectDashboard from "./components/ProjectDashboard";
import ConnectionBanner from "./components/ConnectionBanner";
import BoqToolsPage from "./components/BoqToolsPage";
import "./utils/personalization";
import MainInfoTab from "./components/tabs/MainInfoTab";
import CustomerTab from "./components/tabs/CustomerTab";
import MembersTab from "./components/tabs/MembersTab";
import ParametersTab from "./components/ParametersTab";
import { apiUrl } from "./taskpane";
import { translateStrings } from "./api/translationApi";
import { sendPresence } from "./api/productTablesApi";
import { usePresenceOverview } from "./hooks/usePresenceOverview";
import PresenceBadge from "./components/PresenceBadge";
import { attachAutoTranslator } from "./utils/autoTranslate";
import { setErrorLoggerToken } from "./utils/errorLogger";
// Note: Excel auto-sync of project details (syncProjectToExcel / syncTier0Project)
// was intentionally removed — users didn't want the "Tier 0" and project-named tabs
// to be created automatically on project selection/edit.

const emptyProjectTemplate = () => ({
  id: null,
  name: "",
  location: "",
  customer_id: "",
  date: new Date().toISOString().split("T")[0],
  language: "",
  real_currency: "",
  additional_currency: "",
  course: "",
  building_type: "",
  area: "",
  status: "",
  description: "",
  members: [],
});

const buildCreatePayload = (form) => ({
  name: form.name,
  location: form.location,
  customer_id: Number(form.customer_id),
  date: form.date,
  language: form.language,
  real_currency: form.real_currency,
  additional_currency: form.additional_currency,
  course: form.course === "" ? 0 : Number(form.course),
  building_type: form.building_type,
  area: form.area === "" ? 0 : Number(form.area),
  status: form.status,
  description: form.description,
  members: form.members ?? [],
});

const buildUpdatePayload = (form) => {
  const payload = { ...form };
  delete payload.customer;
  delete payload.memberships;
  delete payload.create_date;
  delete payload.id;
  delete payload.admin;
  delete payload.members;

  return {
    ...payload,
    customer_id: payload.customer_id === "" ? null : Number(payload.customer_id),
    course: payload.course === "" ? null : Number(payload.course),
    area: payload.area === "" ? null : Number(payload.area),
  };
};

export default function TaskpaneApp() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return window.localStorage.getItem("boq.theme") || "dark";
  });

  const [uiLanguage, setUiLanguage] = useState(() => {
    if (typeof window === "undefined") return "geo";
    return window.localStorage.getItem("boq.uiLanguage") || "geo";
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem("boq.theme", theme);
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("boq.uiLanguage", uiLanguage);
    }
  }, [uiLanguage]);

  const withThemeToggle = useCallback(
    (content) => (
      <div className="theme-scope">
        <ConnectionBanner />
        {content}
      </div>
    ),
    []
  );

  const officeReady = useOfficeReady();
  const {
    status: authStatus,
    token,
    isAdmin,
    isSuperuser,
    isAdminOfProject,
    isRedactorOfProject,
    getProjectRole,
    roles,
    databaseGrants,
    refreshRoles,
    authError,
    loginBusy,
    login,
    logout,
    clearAuthError,
    handleUnauthorized,
  } = useAuthManager(officeReady);

  const {
    projects,
    customers,
    refreshProjects,
    refreshCustomers,
    createProject,
    updateProject,
    duplicateProject,
    deleteProject,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  } = useProjectData(token, handleUnauthorized);

  const {
    tables: productTables,
    rowsByTable: productTableRows,
    refreshTables: refreshProductTables,
    loadRows: loadProductTableRows,
    loadRowsTier1: loadProductTableRowsTier1,
    saveRows: saveProductTableRows,
    saveRowsTier1: saveProductTableRowsTier1,
    createTable: createProductTable,
  } = useProductTables(token, handleUnauthorized);

  const { path, route, navigate } = useHashRouter(authStatus);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const root = document.querySelector(".theme-scope");
    if (!root) {
      return undefined;
    }

    return attachAutoTranslator({
      root,
      language: uiLanguage,
      translateBatch: translateStrings,
    });
  }, [uiLanguage, route?.name, authStatus]);

  const [tab, setTab] = useState("Main");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const pendingTabRef = useRef(null);
  const pendingSetupRef = useRef(null); // ribbon "სტრუქტურა": open the setup window once the project page is shown
  const pendingBoqAutoRenderProjectIdRef = useRef(null);

  useEffect(() => {
    if (authStatus === AUTH_STATUS.AUTHENTICATED) {
      refreshProjects().finally(() => setProjectsLoaded(true));
      refreshProductTables().catch(() => undefined);
    }
  }, [authStatus, refreshProjects, refreshProductTables]);

  // Keep error logger in sync with the current auth token
  useEffect(() => {
    setErrorLoggerToken(token);
  }, [token]);

  useEffect(() => {
    if (
      authStatus === AUTH_STATUS.AUTHENTICATED &&
      (route.name === "project-create" || route.name === "project-edit") &&
      customers.length === 0
    ) {
      refreshCustomers();
    }
  }, [authStatus, route.name, customers.length, refreshCustomers]);

  useEffect(() => {
    if (pendingTabRef.current) {
      setTab(pendingTabRef.current);
      pendingTabRef.current = null;
    } else {
      setTab("Main");
    }
    setSaveError("");
  }, [path]);

  useEffect(() => {
    if (route.name === "unknown") {
      navigate(authStatus === AUTH_STATUS.AUTHENTICATED ? ROUTES.PROJECTS : ROUTES.LOGIN);
    }
  }, [route.name, authStatus, navigate]);

  useEffect(() => {
    if (route.name === "logout") {
      logout();
    }
  }, [route.name, logout]);

  const auth = useAuthManager(true);

  window.BOQ = {
    logout: auth.logout,
    showMaterialsPanel: () => navigate(ROUTES.BASES)
  };

  const onlineByProject = usePresenceOverview(token);

  // Presence: while a project is open on any tab, tell the server we are here so colleagues see us
  // online. The BOQ tab sends its own heartbeat (and shows who else is there), so skip it here.
  const presenceProjectId = route.name === "project-edit" ? route.params?.projectId : null;
  useEffect(() => {
    if (!token || !presenceProjectId || tab === "BOQ") return undefined;
    const beat = () => {
      if (document.hidden) return;
      sendPresence(token, presenceProjectId).catch(() => undefined);
    };
    beat();
    const timer = setInterval(beat, 30000);
    return () => clearInterval(timer);
  }, [token, presenceProjectId, tab]);

  const selectedProject = useMemo(() => {
    if (route.name === "project-edit") {
      const projectId = route.params?.projectId;
      if (!projectId) return null;
      return projects.find((item) => String(item.id) === String(projectId)) || null;
    }
    if (route.name === "project-create") {
      return emptyProjectTemplate();
    }
    return null;
  }, [route, projects]);

  // Persist only the currently open project for ribbon render flow.
  // If user leaves project-edit, clear it so ribbon shows alert instead of reopening old project.
  useEffect(() => {
    if (route.name === "project-edit" && route.params?.projectId) {
      window.localStorage.setItem("boq.activeProjectId", route.params.projectId);
    } else if (route.name === "render-boq" || route.name === "delete-project" || route.name === "export-data" || route.name === "auto-evaluate" || route.name === "export-pdf" || route.name === "tools" || route.name === "project-structure") {
      // Keep the current activeProjectId so ribbon flows can read it.
      return;
    } else {
      window.localStorage.removeItem("boq.activeProjectId");
    }
  }, [route.name, route.params?.projectId]);

  useEffect(() => {
    if (route.name === "project-edit" && projects.length > 0 && !selectedProject) {
      navigate(ROUTES.PROJECTS);
    }
  }, [route.name, projects.length, selectedProject, navigate]);

  const handleLogin = useCallback(
    async (credentials) => {
      clearAuthError();
      try {
        await login(credentials);
      } catch (error) {
        // Error message already handled inside the auth manager
      }
    },
    [clearAuthError, login]
  );

  const handleSelectProject = useCallback(
    async (item) => {
      navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${item.id}`);
    },
    [navigate]
  );

  const [structureRefresh, setStructureRefresh] = useState(0);

  // New project / project settings: the setup window from the customer spec (an Office dialog;
  // without dialog support it opens inside the taskpane).
  const openProjectSetup = useCallback(
    async (mode, projectId) => {
      const opened = await openSetupDialog({
        token,
        mode,
        projectId,
        onDone: async ({ projectId: pid }) => {
          await refreshProjects();
          await refreshRoles();
          setStructureRefresh((n) => n + 1);
          setTab("Structure");
          if (pid) navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${pid}`);
        },
      });
      if (!opened) window.location.hash = `#${setupHash({ mode, projectId })}`;
    },
    [token, refreshProjects, refreshRoles, navigate]
  );

  const handleStartCreate = useCallback(() => openProjectSetup("new"), [openProjectSetup]);

  useEffect(() => {
    const pid = pendingSetupRef.current;
    if (route.name === "project-edit" && pid && String(route.params?.projectId) === pid) {
      pendingSetupRef.current = null;
      openProjectSetup("edit", Number(pid));
    }
  }, [route.name, route.params?.projectId, openProjectSetup]);

  const handleBackToProjects = useCallback(() => {
    navigate(ROUTES.PROJECTS);
  }, [navigate]);

  // Opens the web account-management panel (served by the backend at
  // /api/admin-panel). It has its own login, so it runs in a browser window
  // rather than inside the task pane.
  const openAdminPanelWeb = useCallback(() => {
    const url = apiUrl("admin-panel");
    const ui = typeof Office !== "undefined" ? Office?.context?.ui : undefined;
    if (ui && typeof ui.openBrowserWindow === "function") {
      ui.openBrowserWindow(url);
    } else {
      window.open(url, "_blank", "noopener");
    }
  }, []);

  const handleDuplicateProject = useCallback(
    async (projectId, newName) => {
      const created = await duplicateProject(projectId, { name: newName || undefined });
      if (created?.id) {
        navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${created.id}`);
      }
      return created;
    },
    [duplicateProject, navigate]
  );

  const handleProjectSave = useCallback(
    async (form) => {
      if (!token) return;

      setBusy(true);
      setSaveError("");

      try {
        if (route.name === "project-create") {
          await createProject(buildCreatePayload(form));
          // Refresh roles so the creator becomes admin on the new project immediately
          await refreshRoles();
        } else if (route.name === "project-edit" && form?.id) {
          await updateProject(form.id, buildUpdatePayload(form));
        }

        await refreshProjects();
        handleBackToProjects();
      } catch (error) {
        setSaveError(error.message || "Unable to save project.");
      } finally {
        setBusy(false);
      }
    },
    [token, route.name, createProject, updateProject, refreshProjects, refreshRoles, handleBackToProjects]
  );

  if (!officeReady) {
    return withThemeToggle(<div className="loading-screen">Loading Office...</div>);
  }

  if (route.name === "update") {
    return withThemeToggle(
      <AddinUpdatePage
        onBack={authStatus === AUTH_STATUS.AUTHENTICATED ? () => navigate(ROUTES.PROJECTS) : undefined}
      />
    );
  }

  if (authStatus === AUTH_STATUS.CHECKING) {
    return withThemeToggle(<div className="loading-screen">Checking your session...</div>);
  }

  if (route.name === "login") {
    return withThemeToggle(
      <LoginScreen onSubmit={handleLogin} loading={loginBusy} error={authError} />
    );
  }

  if (route.name === "logout") {
    return withThemeToggle(<div className="loading-screen">Signing out...</div>);
  }

  // Bases (მონაცემთა ბაზა) are for redactors/admins, database admins and superusers.
  // Viewer-only accounts (no redactor/admin role on any project) are blocked.
  const hasDatabaseGrants =
    databaseGrants.catalogIds.length > 0 || databaseGrants.resourceKinds.length > 0;
  const canAccessBases =
    isSuperuser ||
    hasDatabaseGrants ||
    (roles || []).some((r) => r.role === "redactor" || r.role === "admin");

  if (route.name === "projects") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--projects">
        <ProjectSelection
          projects={projects}
          onSelect={handleSelectProject}
          onCreate={handleStartCreate}
          onProfile={() => navigate(ROUTES.PROFILE)}
          onSettings={() => navigate(ROUTES.SETTINGS)}
          onManageTables={canAccessBases ? () => navigate(ROUTES.BASES) : undefined}
          onControlPanel={isSuperuser ? openAdminPanelWeb : undefined}
          onLogs={isSuperuser ? () => navigate(ROUTES.LOG) : undefined}
          onSignOut={logout}
          token={token}
        />
      </div>
    );
  }

  if (route.name === "project-create" || route.name === "project-edit") {
    const activeTab = tab;
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--projects taskpane-root--project-editor">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button className="link" onClick={handleBackToProjects}>
            ← Back to projects
          </button>
          {route.name === "project-edit" && selectedProject?.id && (
            <button type="button" className="boq-action-btn" onClick={() => openProjectSetup("edit", selectedProject.id)}>
              ⚙ ნაკვეთი და სტრუქტურა
            </button>
          )}
          {route.name === "project-create" && (
            <button type="button" className="primary" onClick={() => openProjectSetup("new")}>
              ✦ ახალი პროექტის ოსტატი
            </button>
          )}
        </div>

        <TabHeader
          tabs={route.name === "project-create" ? ["Main", "Customer"] : ["Main", "Customer", "Members", "Structure", "BOQ", "Dashboard", "Analytics", "Parameters"]}
          active={tab}
          onChange={setTab}
        />

        {route.name === "project-edit" && tab !== "BOQ" && onlineByProject[String(selectedProject?.id)]?.length > 0 && (
          <div style={{ margin: "6px 0" }}>
            <PresenceBadge people={onlineByProject[String(selectedProject.id)]} />
          </div>
        )}

        {saveError && <div className="msg-error">{saveError}</div>}

        {activeTab === "Main" && selectedProject && (
          <MainInfoTab
            project={selectedProject}
            onSave={handleProjectSave}
            onDelete={() => navigate(ROUTES.DELETE_PROJECT)}
            onDuplicate={handleDuplicateProject}
            isAdmin={
              route.name === "project-create" ||
              isSuperuser ||
              (selectedProject?.id ? isAdminOfProject(selectedProject.id) : false)
            }
            isSuperuser={isSuperuser}
            saving={busy}
            customers={customers}
            mode={route.name === "project-create" ? "create" : "edit"}
          />
        )}

        {tab === "Customer" && (
          <CustomerTab
            customers={customers}
            refreshCustomers={refreshCustomers}
            isSuperuser={isSuperuser}
            onAdd={async (customer) => {
              try {
                await createCustomer(customer);
              } catch (error) {
                setSaveError(error.message || "Unable to add customer.");
                throw error;
              }
            }}
            onUpdate={async (id, data) => {
              try {
                await updateCustomer(id, data);
              } catch (error) {
                setSaveError(error.message || "Unable to update customer.");
                throw error;
              }
            }}
            onDelete={async (id) => {
              try {
                await deleteCustomer(id);
              } catch (error) {
                setSaveError(error.message || "Unable to delete customer.");
                throw error;
              }
            }}
          />
        )}

        {tab === "Structure" && selectedProject?.id && (
          <StructureTab
            token={token}
            project={selectedProject}
            canEdit={isRedactorOfProject(selectedProject.id)}
            onOpenSetup={() => openProjectSetup("edit", selectedProject.id)}
            refreshKey={structureRefresh}
          />
        )}

        {tab === "Members" && selectedProject?.id && (
          <MembersTab
            token={token}
            projectId={selectedProject.id}
            canManage={isSuperuser || isAdminOfProject(selectedProject.id)}
          />
        )}

        {tab === "BOQ" && selectedProject && (
          <BOQManager
            project={selectedProject}
            token={token}
            customers={customers}
            autoRender={
              String(pendingBoqAutoRenderProjectIdRef.current || "") ===
              String(selectedProject.id)
            }
            onAutoRenderDone={() => {
              pendingBoqAutoRenderProjectIdRef.current = null;
            }}
            parentTableId={selectedProject.parent_table_id || null}
            canEdit={
              route.name === "project-create" ||
              (selectedProject?.id
                ? isRedactorOfProject(selectedProject.id)
                : false)
            }
            canManageVersions={
              isSuperuser ||
              (selectedProject?.id ? isAdminOfProject(selectedProject.id) : false)
            }
            userRole={
              selectedProject?.id
                ? getProjectRole(selectedProject.id) ||
                  (isSuperuser ? "admin" : null)
                : null
            }
          />
        )}

        {tab === "Dashboard" && selectedProject?.id && (
          <ProjectDashboard
            project={selectedProject}
            token={token}
            projects={projects}
            onGoToBOQ={() => setTab("BOQ")}
            onSaveBudget={async (budget) => {
              await updateProject(selectedProject.id, {
                cost_config: { ...(selectedProject.cost_config || {}), budget },
              });
              await refreshProjects();
            }}
          />
        )}

        {tab === "Analytics" && selectedProject && (
          <AnalyticsPage
            project={selectedProject}
            token={token}
            tables={productTables}
            rowsByTable={productTableRows}
            loadRows={loadProductTableRows}
            onGoToBOQ={() => setTab("BOQ")}
            projects={projects}
          />
        )}

        {tab === "Parameters" && selectedProject && (
          <ParametersTab
            project={selectedProject}
            onSave={async (costConfig) => {
              await updateProject(selectedProject.id, { cost_config: costConfig });
              await refreshProjects();
              setSaveError("");
            }}
            onGoToBOQ={() => setTab("BOQ")}
          />
        )}
      </div>
    );
  }

  if (route.name === "bases-material" || route.name === "bases-equipment" || route.name === "bases-labour") {
    if (!canAccessBases) {
      return withThemeToggle(
        <div style={{ padding: 30, color: "var(--text-primary)" }}>
          წვდომა აკრძალულია. (Access denied — viewers cannot open bases.)
        </div>
      );
    }
    const kindMap = { "bases-material": "MATERIAL", "bases-equipment": "EQUIPMENT", "bases-labour": "LABOUR" };
    return withThemeToggle(
      <BasesManager
        tables={productTables}
        rowsByTable={productTableRows}
        refreshTables={refreshProductTables}
        loadRows={loadProductTableRows}
        loadRowsTier1={loadProductTableRowsTier1}
        saveRows={saveProductTableRows}
        saveRowsTier1={saveProductTableRowsTier1}
        createTable={createProductTable}
        onBack={() => navigate(ROUTES.PROJECTS)}
        token={token}
        isSuperuser={isSuperuser}
        databaseGrants={databaseGrants}
        initialTab="resources"
        initialKind={kindMap[route.name]}
      />
    );
  }

  if (route.name === "bases" || route.name === "bases-prices") {
    if (!canAccessBases) {
      return withThemeToggle(
        <div style={{ padding: 30, color: "var(--text-primary)" }}>
          წვდომა აკრძალულია. (Access denied — viewers cannot open bases.)
        </div>
      );
    }
    return withThemeToggle(
      <BasesManager
        tables={productTables}
        rowsByTable={productTableRows}
        refreshTables={refreshProductTables}
        loadRows={loadProductTableRows}
        loadRowsTier1={loadProductTableRowsTier1}
        saveRows={saveProductTableRows}
        saveRowsTier1={saveProductTableRowsTier1}
        createTable={createProductTable}
        onBack={() => navigate(ROUTES.PROJECTS)}
        token={token}
        isSuperuser={isSuperuser}
        databaseGrants={databaseGrants}
        initialTab={route.name === "bases-prices" ? "prices" : undefined}
      />
    );
  }

  if (route.name === "profile") {
    return withThemeToggle(
      <ProfilePage
        token={token}
        isAdmin={isAdmin}
        isSuperuser={isSuperuser}
        projects={projects}
        onBack={() => navigate(ROUTES.PROJECTS)}
        onOpenProject={(projectId) => navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${projectId}`)}
        onSignOut={logout}
      />
    );
  }

  if (route.name === "settings") {
    return withThemeToggle(
      <SettingsPage
        theme={theme}
        onSetTheme={setTheme}
        language={uiLanguage}
        onSetLanguage={setUiLanguage}
        onBack={() => navigate(ROUTES.PROJECTS)}
        onProfile={() => navigate(ROUTES.PROFILE)}
        onManageTables={() => navigate(ROUTES.BASES)}
        onSignOut={logout}
      />
    );
  }

  if (route.name === "boq") {
    return withThemeToggle(
      <BOQManager onBack={() => navigate(ROUTES.PROJECTS)} token={token} parentTableId={null} />
    );
  }

  if (route.name === "sheet-lookup") {
    return withThemeToggle(<SheetLookupStandalone token={token} />);
  }

  if (route.name === "log") {
    if (!isSuperuser) return withThemeToggle(<div style={{ padding: 30, color: "var(--text-primary)" }}>Access denied.</div>);
    return withThemeToggle(<LogPage token={token} onBack={() => navigate(ROUTES.PROJECTS)} isAdmin={isAdmin || isSuperuser} />);
  }

  if (route.name === "project-structure") {
    const activeProjectId = window.localStorage.getItem("boq.activeProjectId");
    if (activeProjectId && projects.length === 0) {
      return withThemeToggle(<div className="loading-screen">იტვირთება...</div>);
    }
    const activeProject = activeProjectId ? projects.find((p) => String(p.id) === String(activeProjectId)) : null;
    if (activeProject) {
      pendingTabRef.current = "Structure";
      pendingSetupRef.current = String(activeProject.id);
      navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${activeProject.id}`);
      return null;
    }
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ padding: "32px 24px" }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>პროექტი არ არის არჩეული</div>
        <div style={{ marginBottom: 20, color: "var(--text-muted)" }}>სტრუქტურის შესაცვლელად ჯერ გახსენით პროექტი სიიდან, ან შექმენით ახალი.</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="primary" onClick={() => navigate(ROUTES.PROJECTS)} style={{ borderRadius: 8, padding: "8px 12px" }}>
            პროექტების სია
          </button>
          <button className="boq-action-btn" onClick={() => openProjectSetup("new")}>
            + ახალი პროექტი
          </button>
        </div>
      </div>
    );
  }

  if (route.name === "render-boq") {
    const RB_STRINGS = {
      geo: {
        loading: "იტვირთება...",
        title: "პროექტი არ არის არჩეული",
        body: 'BOQ-ის ჩასატვირთად ჯერ გადადით "პროექტები" განყოფილებაში და გახსენით პროექტი.',
        btn: "პროექტების სია",
      },
      eng: {
        loading: "Loading...",
        title: "No project selected",
        body: 'To load BOQ, go to the "Projects" section and open a project first.',
        btn: "Project list",
      },
      rus: {
        loading: "Загрузка...",
        title: "Проект не выбран",
        body: 'Чтобы загрузить BOQ, перейдите в раздел "Проекты" и откройте проект.',
        btn: "Список проектов",
      },
    };
    const t = RB_STRINGS[uiLanguage] || RB_STRINGS.geo;

    const activeProjectId = window.localStorage.getItem("boq.activeProjectId");

    // Projects haven't loaded yet — wait for them
    if (activeProjectId && projects.length === 0) {
      return withThemeToggle(<div className="loading-screen">{t.loading}</div>);
    }

    const activeProject = activeProjectId
      ? projects.find((p) => String(p.id) === String(activeProjectId))
      : null;

    if (activeProject) {
      pendingTabRef.current = "BOQ";
      pendingBoqAutoRenderProjectIdRef.current = String(activeProject.id);
      navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${activeProject.id}`);
      // Return null to avoid any flash — React will immediately re-render with the new route
      return null;
    }

    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ padding: "32px 24px" }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{t.title}</div>
        <div style={{ marginBottom: 20, color: "var(--text-muted)" }}>{t.body}</div>
        <button
          className="primary"
          onClick={() => navigate(ROUTES.PROJECTS)}
          style={{ borderRadius: 8, padding: "8px 12px" }}
        >
          {t.btn}
        </button>
      </div>
    );
  }

  if (route.name === "export-pdf") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ minHeight: "100vh", padding: "20px 16px" }}>
        <PdfExportLauncher onBack={() => navigate(ROUTES.PROJECTS)} projects={projects} customers={customers} />
      </div>
    );
  }

  if (route.name === "app-errors") {
    if (!isAdmin && !isSuperuser) {
      return withThemeToggle(
        <div style={{ padding: 30, color: "var(--text-primary)" }}>წვდომა აკრძალულია.</div>
      );
    }
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ minHeight: "100vh", padding: "20px 16px" }}>
        <AppErrorLogPage token={token} onBack={() => navigate(ROUTES.PROJECTS)} />
      </div>
    );
  }

  if (route.name === "delete-project") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ minHeight: "100vh", padding: "20px 16px" }}>
        <DeleteProjectPage
          projects={projects}
          deleteProject={deleteProject}
          isAdminOfProject={isAdminOfProject}
          isSuperuser={isSuperuser}
          onBack={() => navigate(ROUTES.PROJECTS)}
          onDeleted={() => navigate(ROUTES.PROJECTS)}
        />
      </div>
    );
  }

  if (route.name === "open-sheets") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ minHeight: "100vh", padding: "20px 16px" }}>
        <OpenSheetsPage onBack={() => navigate(ROUTES.PROJECTS)} />
      </div>
    );
  }

  if (route.name === "work-blocks") {
    return withThemeToggle(
      <BasesManager
        tables={productTables}
        rowsByTable={productTableRows}
        refreshTables={refreshProductTables}
        loadRows={loadProductTableRows}
        loadRowsTier1={loadProductTableRowsTier1}
        saveRows={saveProductTableRows}
        saveRowsTier1={saveProductTableRowsTier1}
        createTable={createProductTable}
        onBack={() => navigate(ROUTES.PROJECTS)}
        token={token}
        isSuperuser={isSuperuser}
        databaseGrants={databaseGrants}
        initialTab="tier0"
      />
    );
  }

  if (route.name === "analytics") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq" style={{ minHeight: "100vh", padding: "20px 16px" }}>
        <AnalyticsPage
          project={selectedProject || projects.find((p) => String(p.id) === String(window.localStorage.getItem("boq.activeProjectId"))) || null}
          token={token}
          tables={productTables}
          rowsByTable={productTableRows}
          loadRows={loadProductTableRows}
          onGoToBOQ={() => navigate(ROUTES.PROJECTS)}
        />
      </div>
    );
  }

  if (route.name === "import-data") {
    return withThemeToggle(
      <ImportSheetPage onBack={() => navigate(ROUTES.PROJECTS)} />
    );
  }

  if (route.name === "export-data") {
    return withThemeToggle(
      <ExportVersionPage
        token={token}
        projects={projects}
        projectsLoaded={projectsLoaded}
        onBack={() => navigate(ROUTES.PROJECTS)}
      />
    );
  }

  if (route.name === "tools") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq">
        <BoqToolsPage projects={projects} token={token} onBack={() => navigate(ROUTES.PROJECTS)} />
      </div>
    );
  }

  if (route.name === "indexation") {
    return withThemeToggle(
      <div className="taskpane-root taskpane-root--boq">
        <IndexationPanel onBack={() => navigate(ROUTES.PROJECTS)} />
      </div>
    );
  }

  if (route.name === "auto-evaluate") {
    return withThemeToggle(
      <AutoEvaluatePage token={token} projects={projects} onBack={() => navigate(ROUTES.PROJECTS)} />
    );
  }

  if (route.name === "control-panel") {
    // Account management is superuser-only (matches the backend /owner/users rules).
    if (!isSuperuser) {
      return withThemeToggle(
        <div style={{ padding: 30, color: "var(--text-primary)" }}>წვდომა აკრძალულია.</div>
      );
    }
    return withThemeToggle(
      <AdminPanelPage
        projects={projects}
        customers={customers}
        isSuperuser={isSuperuser}
        token={token}
        onOpenProject={(projectId) => navigate(`${ROUTES.PROJECT_EDIT_PREFIX}${projectId}`)}
        onBack={() => navigate(ROUTES.PROJECTS)}
      />
    );
  }

  return withThemeToggle(<div className="loading-screen">Preparing application...</div>);
}
